/**
 * handoff.js — the client management hand-off in the browser (LeadMomentum spec
 * docs/superpowers/specs/2026-10-31-quoting-tool-to-proposal-design.md, sections 3, 3b and 4).
 *
 *   Send proposal (compare page): the compared plans, their prices and coverage rows go to
 *   POST /quotes/handoff/comparisons, then client management opens in a new tab and offers them as
 *   a proposal draft. The URL it opens carries no id and no client data: client management asks
 *   this tool's server for the waiting comparison, server to server.
 *
 *   Generate quote (intake page): client management saved the client's details here; the intake
 *   page, opened with ?from=crm, loads them once from GET /quotes/handoff/starts/pending.
 *
 * The comparison is allow-listed key by key, the same list the server re-applies
 * (netlify/functions/shared/handoff-model.js) and client management applies again at its own door.
 * No health answers, doctors, drugs or plan advisor facts. The body stays under the live WAF's
 * 8 KB limit.
 */
import { COPAY_CATEGORIES, loadSelection } from './compare.js';
import { authHeaders } from './agent-identity.js';
import { getStoredIdToken, isTokenValid } from '../auth-config.js';

/** Client management (LeadMomentum's Policy App). No id or client data rides this URL. */
export const CRM_URL = 'https://policies.leadmomentum.net/?from=quoting';
export const MAX_BODY_BYTES = 8 * 1024;
const BUDGET_BYTES = MAX_BODY_BYTES - 256; // headroom for JSON escaping differences
const MAX_TEXT = 80;
const MAX_FEES = 4;
const REF_KEY = 'wqHandoffRef';

function txt(v, max = MAX_TEXT) {
  if (v == null) return undefined;
  const s = String(v).replace(/\s+/g, ' ').trim().slice(0, max);
  return s || undefined;
}

function amount(v) {
  return typeof v === 'number' && Number.isFinite(v) && v >= 0 ? Math.round(v * 100) / 100 : undefined;
}

function https(v) {
  const s = txt(v, 2048);
  return s && /^https:\/\/[^\s<>"']+$/i.test(s) ? s : undefined;
}

function compact(obj) {
  const out = {};
  for (const [k, v] of Object.entries(obj)) if (v !== undefined) out[k] = v;
  return out;
}

function money(n) {
  return '$' + (Number(n) || 0).toFixed(2);
}

/** "Jane Q Doe" → { firstName: 'Jane', lastName: 'Q Doe' }; one word is a first name. */
export function splitName(full) {
  const parts = String(full || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return {};
  return compact({ firstName: txt(parts[0]), lastName: txt(parts.slice(1).join(' ')) });
}

/**
 * What is already inside the monthly price, as short sentences: monthly fees (UHOne FACT dues)
 * first, then riders the agent added on the card.
 */
export function includedTexts(col) {
  const out = [];
  for (const f of Array.isArray(col.fees) ? col.fees : []) {
    if (f && f.name) out.push(`Includes the ${f.name} fee (${money(f.amount)}/mo)`);
  }
  for (const r of Array.isArray(col.riders) ? col.riders : []) {
    if (r && r.name) out.push(`Includes ${r.name} (+${money(r.premium)}/mo)`);
  }
  return out.map((s) => txt(s)).filter(Boolean).slice(0, MAX_FEES);
}

/** One compare-page column → one plan on the wire (section 4). */
export function planFromColumn(col, priceAsOf) {
  const copaysIn = col.copays || {};
  const copays = compact(Object.fromEntries(COPAY_CATEGORIES.map((c) => [c.key, txt(copaysIn[c.key])])));
  const links = compact({
    brochure: https(col.links && col.links.brochure),
    sbc: https(col.links && col.links.sbc),
    provider: https(col.links && col.links.provider),
  });
  const fees = includedTexts(col);
  const coverage = compact({
    network: txt(col.network, 120),
    deductible: amount(col.deductible),
    oopMax: amount(col.oopMax),
    coinsurance: txt(col.coinsurance),
    term: txt(col.term),
    metalTier: txt(col.metalTier),
    productType: txt(col.productTypeLabel),
    ...(Object.keys(copays).length ? { copays } : {}),
    ...(fees.length ? { fees } : {}),
    ...(Object.keys(links).length ? { links } : {}),
  });
  return compact({
    sourcePlanId: txt(col.planId, 200),
    carrierName: txt(col.carrier, 200),
    planName: txt(col.name, 200),
    productType: txt(col.productTypeLabel),
    monthlyPrice: amount(Number(col.premium)),
    priceAsOf,
    ...(Object.keys(coverage).length ? { coverage } : {}),
  });
}

function bytes(obj) {
  return new TextEncoder().encode(JSON.stringify(obj)).length;
}

/**
 * Trim to the WAF budget: links go first (they are long and the client page works without them),
 * then the included-fee lines. Throws when even that does not fit.
 */
export function fitToBudget(payload, budget = BUDGET_BYTES) {
  if (bytes(payload) <= budget) return payload;
  const out = JSON.parse(JSON.stringify(payload));
  for (const p of out.plans) if (p.coverage) delete p.coverage.links;
  if (bytes(out) <= budget) return out;
  for (const p of out.plans) if (p.coverage) delete p.coverage.fees;
  if (bytes(out) <= budget) return out;
  throw new Error('These plans carry too much detail to send at once. Remove one and try again.');
}

// ---------- the client reference from Generate quote ----------

export function saveHandoffRef(clientRef, clientName) {
  try {
    if (!clientRef) sessionStorage.removeItem(REF_KEY);
    else sessionStorage.setItem(REF_KEY, JSON.stringify({ clientRef, clientName: clientName || '' }));
  } catch { /* restricted storage: the proposal asks who it is for instead */ }
}

export function loadHandoffRef() {
  try {
    const v = JSON.parse(sessionStorage.getItem(REF_KEY) || 'null');
    return v && typeof v.clientRef === 'string' ? v : null;
  } catch {
    return null;
  }
}

const sameName = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();

/**
 * The comparison on the wire. The client reference rides only while the intake still holds the
 * client it came with: an agent who switched to another client in the same tab must not send that
 * client's plans to the first client's record.
 */
export function buildComparisonPayload(selection, draft, ref, nowIso = new Date().toISOString()) {
  const columns = (selection && Array.isArray(selection.columns) ? selection.columns : []).slice(0, 4);
  const priceAsOf = selection && selection.savedAt ? new Date(selection.savedAt).toISOString() : nowIso;
  const d = draft || {};
  const client = compact({
    ...splitName(d.clientName),
    phone: txt(d.clientPhone, 30),
    email: txt(d.clientEmail, 254),
  });
  const clientRef = ref && ref.clientRef && sameName(ref.clientName, d.clientName) ? ref.clientRef : undefined;
  return compact({
    clientRef,
    ...(Object.keys(client).length ? { client } : {}),
    plans: columns.map((c) => planFromColumn(c, priceAsOf)),
  });
}

function readDraft() {
  try { return JSON.parse(sessionStorage.getItem('wqQuoteDraft') || 'null'); } catch { return null; }
}

export function isSignedIn() {
  return isTokenValid(getStoredIdToken());
}

/** Save the compared plans for client management. Resolves to { comparisonId, expiresAt }. */
export async function sendProposal({ fetchImpl = fetch } = {}) {
  const sel = loadSelection();
  if (!sel || !Array.isArray(sel.columns) || !sel.columns.length) {
    throw new Error('Nothing to send. Add plans to compare on the results page first.');
  }
  if (!isSignedIn()) throw new Error('Sign in to send a proposal.');
  const payload = fitToBudget(buildComparisonPayload(sel, readDraft(), loadHandoffRef()));
  const res = await fetchImpl('/quotes/handoff/comparisons', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  const parsed = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(parsed.error || ('Send failed: ' + res.status));
  return parsed.data || {};
}

// ---------- Generate quote: fill the intake ----------

const GENDER = { F: 'Female', M: 'Male' };

/** Client management's quote start → the intake draft fields it can fill (section 3b). */
export function draftFromQuoteStart(start) {
  const c = (start && start.client) || {};
  const household = Array.isArray(start && start.household) ? start.household : [];
  const spouse = household.find((m) => m.role === 'spouse');
  const kids = household.filter((m) => m.role === 'dependent');
  const coverageType = spouse && kids.length ? 'family'
    : spouse ? 'individual_spouse'
      : kids.length ? 'individual_children' : 'individual';
  const draft = {
    clientName: [c.firstName, c.lastName].filter(Boolean).join(' '),
    clientEmail: c.email || '',
    clientPhone: c.phone || '',
    dob: c.dob || '',
    zipCode: c.zip || '',
    coverageType,
    householdSize: String(1 + (spouse ? 1 : 0) + kids.length),
    childCount: String(kids.length),
    children: kids.map((k) => ({ dob: k.dob || '', gender: GENDER[k.gender] || 'Male' })),
  };
  if (GENDER[c.gender]) draft.primaryGender = GENDER[c.gender];
  if (spouse) {
    draft.spouseDob = spouse.dob || '';
    if (GENDER[spouse.gender]) draft.spouseGender = GENDER[spouse.gender];
  }
  return draft;
}

/** The waiting quote start, or null. The server hands it out once. */
export async function fetchQuoteStart({ fetchImpl = fetch } = {}) {
  if (!isSignedIn()) return null;
  const res = await fetchImpl('/quotes/handoff/starts/pending', { method: 'GET', headers: authHeaders() });
  if (!res.ok) return null;
  const parsed = await res.json().catch(() => ({}));
  return (parsed.data && parsed.data.start) || null;
}

export const Handoff = {
  CRM_URL,
  splitName,
  includedTexts,
  planFromColumn,
  fitToBudget,
  buildComparisonPayload,
  saveHandoffRef,
  loadHandoffRef,
  isSignedIn,
  sendProposal,
  draftFromQuoteStart,
  fetchQuoteStart,
};
